'use client'

import classNames from 'classnames'
import { FormattedCurrency } from '@dustin-web/microsite-components/src/i18n'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { SourcingCompany } from '@productsite-beta/src/graphql/generated/graphql-types'
import type { ProductPageType } from '@productsite-beta/src/components/product-page'
import {
  useOptimisticPrice,
  useOptimisticCampaign,
} from '../../../product-context/product-optimistic-context'
import ProductVatSwitch from './product-vat-switch'

import './product-price.scss'

type Props = {
  product: ProductPageType
  className?: string
  showMargin?: boolean
}

const ProductPrice = ({ product, className, showMargin = false }: Props) => {
  const { sourcingCompany } = useSiteContext()
  const { translate } = useTranslations()
  const { optimisticPrice } = useOptimisticPrice()
  const { optimisticCampaign } = useOptimisticCampaign()

  const price = product.price
  const isDisabled = !product.availability?.isAvailableForSale

  const showRoundedPrice =
    sourcingCompany === SourcingCompany.Se ||
    sourcingCompany === SourcingCompany.No ||
    sourcingCompany === SourcingCompany.Dk

  // When optimisticPrice is active, we're in optimistic mode — null campaign means no campaign.
  // Only fall back to server-rendered campaign state when no optimistic update is in progress.
  const isCampaign =
    optimisticPrice !== null
      ? optimisticCampaign?.isCampaign ?? false
      : !!price?.isCampaign
  const currentPrice = optimisticPrice ?? price?.price ?? 0
  const originalPrice = optimisticCampaign?.originalPrice ?? price?.originalPrice ?? 0

  const displayOriginalPrice = isCampaign
    ? showRoundedPrice
      ? Math.round(originalPrice)
      : originalPrice
    : showRoundedPrice
      ? Math.round(currentPrice)
      : currentPrice

  const displayCampaignPrice = showRoundedPrice ? Math.round(currentPrice) : currentPrice

  return (
    <div
      className={classNames('c-price-section u-mb-0', {
        'u-disabled': isDisabled,
      })}>
      <div className="u-mb-4">
        <span
          className={classNames('c-price', className, {
            'c-price--original': isCampaign,
          })}>
          <FormattedCurrency amount={displayOriginalPrice} />
          {product.lotQuantity && product.lotQuantity > 0 && (
            <span className="d-block u-fs-12 u-pt-4">
              <FormattedCurrency
                amount={
                  Math.round((currentPrice / product.lotQuantity + Number.EPSILON) * 100) / 100
                }
              />
              /{translate('QuantityUnitShort')}
            </span>
          )}
        </span>
        {isCampaign && (
          <span className="c-price c-price--campaign">
            <FormattedCurrency amount={displayCampaignPrice} />
            {product.lotQuantity && product.lotQuantity > 0 && (
              <span className="d-block u-fs-12 u-pt-4">
                <FormattedCurrency
                  amount={
                    Math.round((currentPrice / product.lotQuantity + Number.EPSILON) * 100) / 100
                  }
                />
                /{translate('QuantityUnitShort')}
              </span>
            )}
          </span>
        )}
      </div>
      <div className="c-pipe-separator align-items-center">
        {showMargin && (
          <div className="d-flex align-items-center">
            <span>
              {product.price?.priceMargin}% {translate('ProductMargin')}
            </span>
          </div>
        )}
        <ProductVatSwitch priceVat={product.price?.priceVat} />
        <div className="d-flex align-items-center">
          {typeof product.delivery?.shippingFee.price === 'number' && (
            <span>
              {translate('ShippingFrom')}{' '}
              <FormattedCurrency amount={product.delivery.shippingFee.price} />
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductPrice
